import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Activity,
  BarChart3,
  BookOpen,
  CalendarDays,
  Image,
  ClipboardCheck,
  Mailbox,
  Truck,
} from "lucide-react";
import { cloneElement, type ReactElement, type ReactNode } from "react";
import {
  Link,
  Navigate,
  Route,
  type RouteProps,
  useLocation,
  useParams,
} from "react-router-dom";

import { CampaignDashboard } from "./CampaignDashboard";
import { ContentLibraryList } from "./ContentLibrary";
import { DocsDetail, DocsIndex } from "./DocsScreen";
import { ContactTaskHistory } from "./ContactTasks";
import { ContactTouches } from "./TouchHistory";
import { ContactLifecycle, LifecycleIndex } from "./LifecycleScreen";
import { LifecycleStages } from "./LifecycleStages";
import {
  CONTACT_LIFECYCLE_PATH,
  CONTACT_TASKS_PATH,
  CONTACT_TOUCHES_PATH,
  touchesPath,
} from "./paths";
import { DoorDetail } from "./DoorDetail";
import { DoorList } from "./DoorList";
import { Drops } from "./Drops";
import { FollowUps } from "./FollowUps";
import { Materials } from "./Materials";
import { OutboundQueue } from "./OutboundQueue";
import { PipelineOverview } from "./PipelineOverview";
import { ReviewQueue } from "./ReviewQueue";
import { ScreenFrame } from "./ScreenFrame";
import { SequenceDetail, SequenceList } from "./Sequences";

/**
 * The Pipeline section: one destination, tabs for each part of it.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE UPSTREAM CONTRACT — one line each, and no more.
 *
 * This is a fork that keeps merging from marmelab/atomic-crm. Everything here
 * lives in our own directory, which upstream never touches. Reaching it needs
 * these injections into upstream files, and nothing else:
 *
 *   1. root/CRM.tsx                  {marketingMobileRoutes}
 *   2. layout/MobileNavigation.tsx   <MarketingNavButton />
 *   3. layout/Header.tsx             <MarketingNavTabs />
 *   4. layout/Header.tsx             <ContactsNavTabs />   (ContactsShell.tsx)
 *
 * Each is one line naming a component we own, so a conflict is a one-line
 * re-add rather than a merge of logic.
 *
 * Those files are among the most-churned in the repo -- CRM.tsx 53 upstream
 * commits a year, MobileNavigation.tsx 26 -- so the danger is not a noisy
 * conflict. It is a QUIET one: a merge that resolves by taking upstream's
 * version of a file and silently drops our line. The app still builds, still
 * deploys, and simply has no marketing screens.
 *
 * check-injections.mjs exists to catch exactly that, and deploy_crm.sh runs it
 * before every deploy. If it fails after a merge, re-add the line it names.
 * Do not delete the check.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * WHY ONE HEADER ENTRY INSTEAD OF FIVE
 * ------------------------------------
 * These screens were five top-level tabs, which put nine across the header
 * alongside upstream's four and read as nine unrelated destinations. They are
 * one workflow -- collect, review, plan, send, measure -- so they are now one
 * entry with tabs inside it.
 *
 * Schedule and Drops were later merged into a single Drops tab: they were two
 * stages of one action (a date, then rows, then postage) and splitting them
 * meant neither screen could answer "what is actually going out". The old
 * /marketing/schedule path redirects rather than 404s, so existing links live.
 */

/**
 * Route paths now live in ./paths so screens can link to each other without
 * importing this module, which imports them -- see paths.ts for why that cycle
 * matters. Re-exported here so every existing import keeps working.
 */
export { MARKETING_PATHS } from "./paths";
import { isMarketingSection, MARKETING_PATHS } from "./paths";

/**
 * The six pipeline destinations, in the order they are shown.
 *
 * EXPORTED so there is exactly ONE list. It drives PipelineShell's tab strip,
 * MarketingNavButton's mobile menu, and mobileNav.test.tsx's assertion about
 * that menu -- so a screen added here appears in all three with no second
 * edit, and a test that only ever saw five of six cannot pass.
 */
export const PIPELINE_SCREENS = [
  {
    path: MARKETING_PATHS.overview,
    label: "Overview",
    Icon: Activity,
    blurb: "Runs, what changed, and a manual trigger",
  },
  {
    path: MARKETING_PATHS.review,
    label: "Review",
    Icon: ClipboardCheck,
    blurb: "Answer merge and suppression questions",
  },
  {
    path: MARKETING_PATHS.doors,
    label: "Doors",
    Icon: Mailbox,
    blurb: "The mailing universe, and opt-outs",
  },
  {
    path: MARKETING_PATHS.materials,
    label: "Materials",
    Icon: Image,
    blurb: "Copy, URLs, QR targets and artwork",
  },
  {
    path: MARKETING_PATHS.drops,
    label: "Drops",
    Icon: CalendarDays,
    blurb: "Schedule, plan and send",
  },
  {
    path: MARKETING_PATHS.campaign,
    label: "Campaign",
    Icon: BarChart3,
    blurb: "Spend, response and the holdout",
  },
];

/**
 * Is this tab the active one?
 *
 * Overview is an exact match because its path is the section root and would
 * otherwise prefix-match every other tab. The rest match their own subtree, so
 * a detail page such as /marketing/docs/02-pipeline keeps its parent tab lit.
 */
const isActive = (pathname: string, path: string) =>
  path === MARKETING_PATHS.overview
    ? pathname === path || pathname === `${path}/`
    : pathname === path || pathname.startsWith(`${path}/`);

/**
 * The shell every pipeline screen renders inside.
 *
 * Self-contained rather than reusing upstream's tabs primitive: these tabs are
 * ROUTES, not local state, so the URL is the source of truth and a shared
 * component that owns its own selection would fight it.
 */
const PipelineShell = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return (
    /**
     * ONE column, owned here, so the tabs and the content below them share a
     * left edge by construction.
     *
     * They did not before: the tab bar spanned the full width while each
     * screen centered its own narrower column, so the tabs began several
     * hundred pixels left of the content they belonged to and read as page
     * furniture rather than as that content's own tabs.
     *
     * Screens therefore must NOT set their own max-width or mx-auto. If one
     * does, its content drifts out of line with the tabs again.
     */
    <div className="flex flex-col min-w-0 w-full max-w-5xl mx-auto">
      <div className="flex gap-1 overflow-x-auto border-b">
        {PIPELINE_SCREENS.map(({ path, label, Icon }) => {
          const active = isActive(pathname, path);
          return (
            <Link
              key={path}
              to={path}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors",
                active
                  ? "border-foreground font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="size-4" />
              {label}
            </Link>
          );
        })}
      </div>
      <div className="pt-4">{children}</div>
    </div>
  );
};

const wrap = (node: ReactNode) => <PipelineShell>{node}</PipelineShell>;

/**
 * The same column PipelineShell owns, WITHOUT its tabs.
 *
 * Docs sits under /marketing for historical reasons only. MarketingNavTabs
 * already stops Pipeline claiming the top-level tab for it (see `onDocs`
 * below), but the routes were still wrapped in PipelineShell -- so the
 * pipeline's own sub-navigation, which is about door-to-door mailing runs,
 * appeared above a page of prose about the whole project. Two navigations
 * disagreeing about where you are is worse than either alone.
 *
 * The TABS were the wrong part, not the column. Dropping the wrapper outright
 * would leave Docs full-bleed while every sibling screen is max-w-5xl, so the
 * column stays and only the tab bar goes.
 */
const PlainShell = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col min-w-0 w-full max-w-5xl mx-auto">
    <div className="pt-4">{children}</div>
  </div>
);

const wrapPlain = (node: ReactNode) => <PlainShell>{node}</PlainShell>;

/**
 * The old touch-history URL, forwarded to the new one WITH ITS ID.
 *
 * WHY THIS IS A COMPONENT AND NOT A BARE <Navigate>, WHICH IS WHAT THE OTHER
 * TWO LEGACY ROUTES USE.
 *
 * mk-schedule-legacy and mk-content-legacy are static-to-static: `to` is a
 * literal path and nothing has to be carried across. This one is not.
 * `<Navigate to>` performs no path interpolation, so
 * `<Navigate to={CONTACT_TOUCHES_PATH} />` navigates to the seven literal
 * characters `/contacts/:id/touches`, which matches no route -- a 404 in
 * place of the 404 it was added to fix, and one that would look correct in
 * review because it reads exactly like its two neighbors.
 *
 * So the param is read here and the destination built with touchesPath, the
 * same helper every in-app link to the page already uses. A future rename of
 * the touches route therefore moves this redirect with it.
 */
const LegacyTouchesRedirect = () => {
  const { id } = useParams();
  // The route pattern cannot match without an `:id`, so the fallback is not
  // reachable today. It is here because the alternative when it ever is --
  // touchesPath(undefined) -- builds "/contacts/undefined/touches", a page
  // that mounts and then asks the data provider for a contact named
  // "undefined". Failing to the hub is a worse URL and a better outcome.
  return id ? (
    <Navigate to={touchesPath(id)} replace />
  ) : (
    <Navigate to={MARKETING_PATHS.hub} replace />
  );
};

/**
 * Put every route element inside a ScreenFrame.
 *
 * APPLIED TO THE ARRAY, NOT AT THE CALL SITES, and that is the whole design.
 * There are three registration shapes below -- wrap() for a pipeline screen,
 * wrapPlain() for Docs, and a bare element for the twelve routes that need
 * neither -- so a frame written at each call site is a frame the next screen
 * can be added without. That is exactly how the state this replaces arose:
 * five screens, three different answers, and the two that looked right on a
 * phone looked right because somebody had hand-written `p-4`. Mapping the
 * array makes an unframed route impossible to write rather than merely
 * discouraged, and screenInset.test.tsx measures one route of each shape.
 *
 * THE THREE REDIRECT ROUTES ARE FRAMED TOO. <Navigate> renders nothing, so
 * their frame is an empty padded box for the single render before it
 * navigates. The alternative is a list of exceptions, and a list of
 * exceptions is the thing that goes stale.
 *
 * cloneElement keeps `key` and `path`, which is what <CustomRoutes> reads.
 */
const framed = (routes: ReactElement<RouteProps>[]) =>
  routes.map((route) =>
    cloneElement(route, {
      element: <ScreenFrame>{route.props.element}</ScreenFrame>,
    }),
  );

/**
 * The routes, as an array of <Route> elements.
 *
 * Registered in BOTH the desktop and mobile <Admin> blocks, so the two
 * surfaces share one set of paths and a nav link can never point at a route
 * that only exists on the other. (The name says "mobile" for historical
 * reasons; it serves both, and is left alone because it is an injection point.)
 *
 * An array rather than a wrapper component on purpose: react-admin's
 * <CustomRoutes> inspects its children and expects <Route> elements, so a
 * component returning a fragment would not register.
 */
export const marketingMobileRoutes = framed([
  <Route
    key="mk-overview"
    path={MARKETING_PATHS.overview}
    element={wrap(<PipelineOverview />)}
  />,
  <Route
    key="mk-doors"
    path={MARKETING_PATHS.doors}
    element={wrap(<DoorList />)}
  />,
  // Flat, not nested: CustomRoutes takes a list of <Route> elements and would
  // never register a nested child. Same reason the docs detail route is flat.
  <Route
    key="mk-door"
    path={MARKETING_PATHS.door}
    element={wrap(<DoorDetail />)}
  />,
  <Route
    key="mk-review"
    path={MARKETING_PATHS.review}
    element={wrap(<ReviewQueue />)}
  />,
  <Route
    key="mk-materials"
    path={MARKETING_PATHS.materials}
    element={wrap(<Materials />)}
  />,
  <Route
    key="mk-drops"
    path={MARKETING_PATHS.drops}
    element={wrap(<Drops />)}
  />,
  // The old Schedule path, kept alive as a redirect. Deleting it would break
  // every bookmark and every link in the docs at once, and a 404 is a worse
  // answer than the screen the content moved to.
  <Route
    key="mk-schedule-legacy"
    path={MARKETING_PATHS.scheduleLegacy}
    element={<Navigate to={MARKETING_PATHS.drops} replace />}
  />,
  <Route
    key="mk-campaign"
    path={MARKETING_PATHS.campaign}
    element={wrap(<CampaignDashboard />)}
  />,
  <Route
    key="mk-docs"
    path={MARKETING_PATHS.docs}
    element={wrapPlain(<DocsIndex />)}
  />,
  // A separate route rather than a nested one: CustomRoutes takes a flat list
  // of <Route> elements, so a nested child would never be registered.
  <Route
    key="mk-doc"
    path={`${MARKETING_PATHS.docs}/:slug`}
    element={wrapPlain(<DocsDetail />)}
  />,
  // NOT wrapped in PipelineShell, and not under /marketing: this is a page
  // about one contact. Wrapping it put the pipeline's sub-navigation above
  // someone's touch history and lit the Pipeline tab in the header.
  <Route
    key="mk-touches"
    path={CONTACT_TOUCHES_PATH}
    element={<ContactTouches />}
  />,
  // Where that page used to be, kept alive as a redirect exactly like
  // mk-schedule-legacy and mk-content-legacy -- and registered ADJACENT to
  // the route it forwards to, so the next person to move the page sees both
  // halves at once rather than leaving a redirect pointing at nothing.
  //
  // The element is a component rather than a bare <Navigate> because this
  // path carries an :id that has to survive the move. See
  // LegacyTouchesRedirect above.
  <Route
    key="mk-touches-legacy"
    path={MARKETING_PATHS.touchesLegacy}
    element={<LegacyTouchesRedirect />}
  />,
  // Every task for one contact, open and closed. Registered just after the
  // touch history and built the same way, because it is the same screen for a
  // different log: the contact page shows only what is outstanding, and this
  // is where "did we actually follow up, and what did we say?" is answered.
  //
  // NOT wrapped in PipelineShell, and not under /marketing, for the reason
  // mk-touches gives above: this is a page about one PERSON, and wrapping it
  // would put the door-to-door sub-navigation over it and light Pipeline in
  // the header.
  //
  // NO TAB TO SHIP ALONGSIDE, and that is deliberate rather than an omission.
  // Like the touch history it belongs to ONE contact, so it is reached from
  // that contact's own page -- ContactTasksPanel's "See all N tasks" link --
  // and from the lifecycle screen's link to the task a step wrote. A
  // CONTACTS_SCREENS tab would need a contact to point at and has none.
  <Route
    key="mk-contact-tasks"
    path={CONTACT_TASKS_PATH}
    element={<ContactTaskHistory />}
  />,
  // One contact's lifecycle: their touches, the plan their enrollment will
  // actually walk, and the stage, cadence and pause that decide it.
  //
  // NOT wrapped in PipelineShell, and not under /marketing, for the reason
  // mk-touches gives above: this is a page about one PERSON, and wrapping it
  // would put the door-to-door sub-navigation over it and light Pipeline in
  // the header.
  //
  // Registered in the SAME COMMIT as its tab in ContactsShell.tsx and as the
  // picker below. A tab without a route is a link to a 404; a route without a
  // tab is unreachable; and this pair in particular is a screen the contact
  // panel links to by hand, so a missing route here is a dead link on every
  // contact page rather than merely an absent tab.
  <Route
    key="mk-lifecycle-index"
    path={MARKETING_PATHS.lifecycle}
    element={<LifecycleIndex />}
  />,
  // BEFORE the dynamic one, and adjacent to it. React Router v6 ranks a
  // static segment above a dynamic one, so /contacts/lifecycle reaches the
  // picker whichever order these two are in -- but keeping them together is
  // how the next reader sees that they are one screen and its landing page
  // rather than two unrelated registrations. Same reasoning as mk-sequences
  // and mk-sequence below.
  <Route
    key="mk-lifecycle"
    path={CONTACT_LIFECYCLE_PATH}
    element={<ContactLifecycle />}
  />,
  // The cadence settings behind all of the above: how long a stage lets
  // somebody sit untouched, what the triage task should say, and the order the
  // stages are shown in. Registered in the SAME COMMIT as its tab in
  // ContactsShell.tsx, for the reason mk-followups gives below -- a tab
  // without a route is a link to a 404 and a route without a tab is
  // unreachable.
  //
  // A SIBLING PATH, /contacts/lifecycle-stages, deliberately not nested under
  // /contacts/lifecycle: ContactsShell matches a tab by prefix, so a nested
  // path would be claimed by the Lifecycle tab and this one could never light.
  // See paths.ts.
  //
  // NO SHELL, like mk-lifecycle above and for the same reason: PipelineShell's
  // tabs are about door-to-door mailing runs, and these settings decide who
  // gets chased.
  <Route
    key="mk-lifecycle-stages"
    path={MARKETING_PATHS.lifecycleStages}
    element={<LifecycleStages />}
  />,
  // The content library, under Contacts. Templates are what gets sent to
  // PEOPLE, and the library spent months behind a tab bar about door-to-door
  // mailing runs, where nobody looked for it.
  //
  // NO SHELL, for the same reason as mk-touches above: wrapping it would put
  // the pipeline's tabs over a contacts page and light Pipeline in the header.
  // Its sub-navigation comes from ContactsNavTabs in layout/Header.tsx, and
  // its column and inset come from the ScreenFrame that framed() puts around
  // every route in this array -- which is where "it needs no wrapper of ours
  // at all, layout/Layout.tsx already gives every page a column" used to be,
  // and that was only ever true on DESKTOP. MobileLayout gives no column at
  // all, so on a phone this screen ran edge to edge. See ScreenFrame.tsx.
  //
  // Create and Edit are still reached through the content_assets Resource's
  // own routes (/content_assets/create, /content_assets/:id) registered in
  // CRM.tsx; this custom path only gives the list its place in the sub-nav.
  <Route
    key="mk-content"
    path={MARKETING_PATHS.contactContent}
    element={<ContentLibraryList />}
  />,
  // The follow-up queue, under Contacts. Registered in the SAME commit as its
  // tab in ContactsShell.tsx, and that pairing is the point: a tab without a
  // route is a link to a 404 and a route without a tab is unreachable, so
  // neither ships alone.
  //
  // NO SHELL, like mk-touches and mk-content above. Wrapping it in
  // PipelineShell would put the pipeline's tabs over a page about contacts
  // and light Pipeline in the header. Its sub-navigation is ContactsNavTabs
  // in layout/Header.tsx; its column and inset are the ScreenFrame framed()
  // puts around every route here.
  <Route
    key="mk-followups"
    path={MARKETING_PATHS.followups}
    element={<FollowUps />}
  />,
  // Sequences, under Contacts. Registered in the SAME commit as its tab in
  // ContactsShell.tsx, for the reason mk-followups gives above: a tab without
  // a route is a link to a 404 and a route without a tab is unreachable.
  //
  // NO SHELL, like mk-followups and mk-content.
  <Route
    key="mk-sequences"
    path={MARKETING_PATHS.sequences}
    element={<SequenceList />}
  />,
  // Flat, not nested: CustomRoutes takes a list of <Route> elements and would
  // never register a nested child. Same reason mk-door and mk-doc are flat.
  //
  // AFTER the list route, and that ordering is not decorative on a react-router
  // v6 tree -- ranked matching would sort a static segment above a dynamic one
  // anyway, but keeping the pair adjacent is how the next reader sees that
  // /contacts/sequences and /contacts/sequences/3 are one screen and its
  // detail rather than two unrelated registrations.
  <Route
    key="mk-sequence"
    path={MARKETING_PATHS.sequence}
    element={<SequenceDetail />}
  />,
  // The outbound email queue, under Contacts. Registered in the SAME commit as
  // its tab in ContactsShell.tsx, for the reason mk-followups gives above.
  //
  // NO SHELL, like mk-followups and mk-sequences. It is a page about messages
  // to PEOPLE; PipelineShell's tabs are about door-to-door mailing runs, and
  // wrapping it would put those over it and light Pipeline in the header.
  <Route
    key="mk-outbound"
    path={MARKETING_PATHS.outbound}
    element={<OutboundQueue />}
  />,
  // The old pipeline path, kept alive as a redirect, exactly like
  // mk-schedule-legacy. Deleting it would break every bookmark and every docs
  // link at once, and a 404 is a worse answer than the screen it moved to.
  <Route
    key="mk-content-legacy"
    path={MARKETING_PATHS.contentLegacy}
    element={<Navigate to={MARKETING_PATHS.contactContent} replace />}
  />,
]);

/**
 * Desktop header entry — ONE tab for the whole section.
 *
 * Atomic CRM's Header hardcodes its tabs rather than deriving them from
 * registered resources, so a <Resource> alone renders a reachable route with no
 * way to reach it -- which is exactly what happened once: the screens were live
 * on desktop and invisible for a whole session.
 *
 * Self-contained rather than importing their NavigationTab, which is not
 * exported. The styling is duplicated deliberately: depending on an unexported
 * internal breaks on any refactor, a dozen lines of Tailwind do not.
 */
export const MarketingNavTabs = () => {
  const { pathname } = useLocation();
  // Pipeline must NOT claim the Docs routes, which sit under the same /marketing
  // prefix for historical reasons. Checking docs first settles it.
  const onDocs = pathname.startsWith(MARKETING_PATHS.docs);
  const cls = (on: boolean) =>
    `px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
      on
        ? "text-secondary-foreground border-secondary-foreground"
        : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
    }`;
  return (
    <>
      <Link
        to={MARKETING_PATHS.overview}
        className={cls(pathname.startsWith(MARKETING_PATHS.hub) && !onDocs)}
      >
        Pipeline
      </Link>
      <Link to={MARKETING_PATHS.docs} className={cls(onDocs)}>
        Docs
      </Link>
    </>
  );
};

/**
 * Bottom-bar entry. ONE slot, with the whole section behind a menu.
 *
 * WHY ONE AND NOT TWO
 * -------------------
 * It was two -- Pipeline and Docs -- and the seven items that made are what
 * clipped the bar off both ends in production (see mobileNav.test.tsx and
 * c2c12ea4). That commit made the row size itself, so seven items FIT; they
 * fit tightly, and the numbers it measured are the argument for this change:
 * at 320px the narrowest slot was 35.4px against Apple's 44pt touch target,
 * with 22px of slack in the whole row.
 *
 * Six slots instead of seven is the width. The menu is the reach.
 *
 * AND THE MENU IS A GAIN, NOT A CONSOLATION. On mobile the six pipeline
 * screens were reachable ONLY through PipelineShell's tab strip, which is
 * `flex gap-1 overflow-x-auto`: at 320px the later tabs are off the right
 * edge until you drag a strip nothing tells you is draggable. One tap now
 * lists all six at once, at `h-12` each -- taller than the 61px bar's own
 * slots and well over the touch guidance the bar itself cannot meet.
 *
 * DRIVEN BY `PIPELINE_SCREENS`, DELIBERATELY. A hand-written second list is
 * a list that goes stale: adding a pipeline screen would add its tab and
 * silently not add its menu entry, and nothing would fail. The menu is the
 * same array PipelineShell's tabs are built from, so the two cannot
 * disagree, and mobileNav.test.tsx derives its assertion from that array as
 * well rather than counting to six.
 *
 * DOCS IS IN THE MENU BUT NOT IN `PIPELINE_SCREENS`, and the separator is
 * load-bearing rather than decoration. Docs is not a PipelineShell tab --
 * PlainShell above records why at length: the pipeline's sub-navigation is
 * about door-to-door mailing runs and sat over a page of prose about the
 * whole project, two navigations disagreeing about where you are. Putting
 * Docs in that array to shorten this component would put those tabs back. So
 * it is appended, below a separator that says the same thing to the user
 * that the code says here.
 *
 * ONE ENTRY, ONE INJECTION. This used to return a FRAGMENT of two buttons for
 * that reason; it now returns a single element and the injected line is
 * unchanged either way. Keep the export NAME whatever the component becomes
 * inside -- check-injections.mjs greps upstream's nav for `MarketingNavButton`
 * and a rename is a silently missing section after the next merge.
 *
 * Measured after the change, in mobileNav.test.tsx:
 *   320px  54.4 49.4 [68 FAB] 49.4 49.4 49.4 = 320, nothing clipped
 *          (re-measured; this line read 53.2 49.7 and did not reproduce)
 *   390px  every slot 64.4
 *   767px  every slot capped at 68, cluster centered
 *   the menu at 320x700: 151.6 x 377, top 258.0, bottom 635.0 against a bar
 *   top of 638.7 -- opens upward, wholly on screen, and does not scroll
 *
 * The narrowest slot went from 35.4 to 49.4, so every one of the six now
 * clears Apple's 44pt touch target on BOTH axes -- which mobileNav.test.tsx
 * asserts as the guidance itself rather than as whatever number the layout
 * happened to produce.
 */
export const MarketingNavButton = () => {
  const { pathname } = useLocation();
  // ONE predicate for the whole section, Docs included -- the button is now
  // the only bar entry that leads there, so it must light anywhere under
  // /marketing. The two buttons it replaced split this between
  // `startsWith(hub) && !onDocs` and `onDocs`, which is the same test written
  // as a mutually exclusive pair only because there were two lamps to drive.
  const on = isMarketingSection(pathname);

  // Shared by every destination in the menu, and matched to the create FAB's
  // menu in layout/MobileNavigation.tsx class for class: two menus opening
  // out of the same 61px bar that sized their rows differently would read as
  // two applications.
  const item = "h-12 px-4 text-base";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            // MUST MATCH NavigationButton in layout/MobileNavigation.tsx,
            // class for class. This button sits in the SAME flex row as
            // upstream's five, so a slot that sizes differently from the rest
            // re-creates the overflow that row was fixed for -- and it
            // re-creates it as a clip at both ends of the bar, which is
            // silent.
            //
            // The full reasoning is in that file. The short version: no fixed
            // width; `basis-0 grow` to share the row out; `has-[>svg]:px-0`
            // to cancel the base size's `has-[>svg]:px-3`, which a plain
            // `px-0` does NOT touch because tailwind-merge scopes groups by
            // modifier; and NO `min-w-0`, so `min-width: auto` floors every
            // slot at its own label and a nowrap label can never be clipped
            // or pushed onto its neighbor.
            "flex-col gap-1 h-auto py-2 has-[>svg]:px-0 rounded-md basis-0 grow max-w-16",
            on ? null : "text-muted-foreground",
          )}
        >
          <Truck className="size-6" />
          <span className="text-[0.6rem] font-medium">Pipeline</span>
        </Button>
      </DropdownMenuTrigger>
      {/*
        UPWARD, EXPLICITLY -- and this prop's mutation came back GREEN, which
        is worth saying rather than hiding. The bar is `fixed bottom-0`, so a
        menu on Radix's default `side="bottom"` is asking to be drawn below
        the bottom of the viewport; Radix's collision handling then flips it
        and the rendered result is byte-for-byte what `side="top"` produces,
        so deleting this changes nothing measurable today.
        It is kept anyway, and NOT as the insurance this repo usually deletes:
        `shrink` and a plain `px-0` went out of the sibling class string
        because they could never do anything at all, whereas this is the
        difference between asking for the right placement and asking for the
        wrong one and being corrected. Mutation MB9 is that distinction made
        concrete -- `side="bottom" avoidCollisions={false}` renders the menu
        off the bottom of the screen and the assertion below catches it, so
        the request IS what wins once the correction is not there.
        Two neighbors of it were deleted for the usual reason: `align="center"`
        is Radix's own default written out, and `collisionPadding={8}` is slack
        that is never consumed -- the menu settles 258px below the top of a
        700px viewport. Both mutations were green.
      */}
      <DropdownMenuContent side="top">
        {PIPELINE_SCREENS.map(({ path, label, Icon }) => (
          <DropdownMenuItem key={path} asChild className={item}>
            {/*
              A real <Link>, not an `onSelect` that navigates. The create
              FAB's items open sheets and have nothing to link to; these are
              destinations, so they get an anchor -- which is also what makes
              them long-pressable and openable in a new tab.
            */}
            <Link to={path}>
              <Icon className="size-4" />
              {label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className={item}>
          <Link to={MARKETING_PATHS.docs}>
            <BookOpen className="size-4" />
            Docs
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
